import { Box, Button } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";

const FOOTER_BUTTON_FONT_SIZE_PX = 9.75;
const FOOTER_BUTTON_HEIGHT_PX = 26;
const FOOTER_BUTTON_RADIUS_PX = 6;

/** Shared size and type for dialog footer actions (Clear All / Cancel / Apply Filters). */
export function footerButtonMetrics() {
  return {
    boxSizing: "border-box" as const,
    height: FOOTER_BUTTON_HEIGHT_PX,
    minHeight: FOOTER_BUTTON_HEIGHT_PX,
    maxHeight: FOOTER_BUTTON_HEIGHT_PX,
    py: 0,
    textTransform: "none" as const,
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    fontSize: FOOTER_BUTTON_FONT_SIZE_PX,
    lineHeight: 1,
    borderRadius: `${FOOTER_BUTTON_RADIUS_PX}px`,
  };
}

/** Text footer action, same chrome as Advanced Search "Clear All". */
export function footerTextButtonSx(theme: Theme) {
  return {
    textTransform: "none" as const,
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    fontSize: FOOTER_BUTTON_FONT_SIZE_PX,
    lineHeight: 1,
    borderRadius: `${FOOTER_BUTTON_RADIUS_PX}px`,
    color: theme.app.dialog.listToggleTextColor,
  };
}

export function footerCancelButtonSx(theme: Theme) {
  const outline = theme.app.filter.toggleTab.activeColor;
  return {
    ...footerButtonMetrics(),
    color: outline,
    borderColor: alpha(outline, 0.5),
    "&:hover": {
      borderColor: outline,
      backgroundColor: theme.app.dialog.footerOutlinedHoverBackground,
      color: outline,
    },
  };
}

export function footerPrimaryButtonSx(theme: Theme, destructive = false) {
  const colors = confirmButtonColors(theme, destructive);
  return {
    ...footerButtonMetrics(),
    backgroundColor: colors.backgroundColor,
    color: colors.color,
    boxShadow: colors.boxShadow,
    "&:hover": {
      backgroundColor: colors.backgroundColor,
      color: colors.color,
      boxShadow: colors.boxShadow,
    },
  };
}

interface DialogFooterButtonsProps {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  cancelDisabled?: boolean;
  confirmDisabled?: boolean;
  /** Enabled confirm uses the selected Taxable red instead of the primary accent. */
  confirmDestructive?: boolean;
}

function confirmButtonColors(theme: Theme, destructive: boolean) {
  if (destructive) {
    return {
      backgroundColor: theme.app.dialog.listToggleErrorBackground,
      color: theme.app.dialog.listToggleSelectedTextColor,
      boxShadow: theme.app.dialog.listToggleErrorShadow,
    };
  }
  return {
    backgroundColor: theme.app.dialog.footerPrimaryButtonBackground,
    color: theme.app.dialog.headerTextColor,
    boxShadow: theme.app.dialog.footerPrimaryButtonShadow,
  };
}

export default function DialogFooterButtons({
  onCancel,
  onConfirm,
  confirmLabel,
  cancelDisabled = false,
  confirmDisabled = false,
  confirmDestructive = false,
}: Readonly<DialogFooterButtonsProps>) {
  return (
    <>
      <Box sx={{ flex: 1 }} />
      <Button
        onClick={onCancel}
        disabled={cancelDisabled}
        variant="outlined"
        size="small"
        sx={footerCancelButtonSx}
      >
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        size="small"
        disableElevation
        disabled={confirmDisabled}
        sx={(theme) => footerPrimaryButtonSx(theme, confirmDestructive)}
      >
        {confirmLabel}
      </Button>
    </>
  );
}
